import React, { useState, useEffect } from 'react';
import { Search, Headphones, BookOpen, User, Menu, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../api-config';
import axios from 'axios';
import './style.css';
import { FaSignOutAlt } from 'react-icons/fa';
import { handleAuthError } from '../hooks/ValidateToken';

const Searcher = () => {
  const [searchValue, setSearchValue] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [generos, setGeneros] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    // Obtener datos del usuario
    axios
      .get(`${API_URL}/user/me`, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const user = response.data;
        setUserId(user.id);
        let roles = response.data.roles;
        if (roles.includes('Admin')) {
          setAdmin(true);
        }
      })
      .catch((error) => {
          console.log(error)
          handleAuthError(error, navigate);
      });

    axios
      .get(`${API_URL}/genres`)
      .then((response) => {
        setGeneros(response.data || []);
      })
      .catch((error) => {
        console.error('Error al cargar géneros:', error);
        handleAuthError(error, navigate);
      });
  }, []);

  const handleSearch = () => {
    if (userId) {
      navigate(`/results-list/${userId}`, { state: { query: searchValue } });
    } else {
      console.error('User ID not available');
    }
  };

  const goToMyAudiobooks = () => {
    if (userId) {
      navigate(`/mis-audiolibros/${userId}`);
    } else {
      console.error('User ID not available');
    }
  };

  const goToProfile = () => {
    navigate('/perfil');
  };

  const goToUsers = () => {
    navigate('/users');
  };

  const goToGenreBooks = (genre) => {
    const safeGenre = genre.replace(/\//g, '-');
    navigate(`/books-by-genre/${encodeURIComponent(safeGenre)}/${userId}`);
  };

  const handleKeyDown = (genre, event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      goToGenreBooks(genre);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex" role="main">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setIsSidebarOpen(false)} // Cierra el sidebar al hacer clic en el overlay
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar */}
      <nav
        aria-label="Menú principal"
        className={`fixed top-0 left-0 h-full w-64 bg-slate-800 shadow-lg transform transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 z-20`}
        role="navigation"
      >
        <div className="flex flex-col justify-between h-full p-6">
          <div>
            <h2 className="text-xl font-bold text-purple-400 mb-6" id="menu-title">
              Menú
            </h2>
            <ul className="space-y-4" aria-labelledby="menu-title">
              <li
                onClick={goToProfile}
                className="flex items-center space-x-2 text-white cursor-pointer hover:text-purple-400"
                role="button"
                tabIndex="0"
                aria-label="Ir al perfil"
              >
                <User className="w-5 h-5" aria-hidden="true" />
                <span>Perfil</span>
              </li>
              <li
                onClick={goToMyAudiobooks}
                className="flex items-center space-x-2 text-white cursor-pointer hover:text-purple-400"
                role="button"
                tabIndex="0"
                aria-label="Ir a mis audiolibros"
              >
                <BookOpen className="w-5 h-5" aria-hidden="true" />
                <span>Mis Audiolibros</span>
              </li>
              {admin && (
                <li
                  onClick={goToUsers}
                  className="flex items-center space-x-2 text-white cursor-pointer hover:text-purple-400"
                  role="button"
                  tabIndex="0"
                  aria-label="Ir a la gestión de usuarios"
                >
                  <Users className="w-5 h-5" aria-hidden="true" />
                  <span>Usuarios</span>
                </li>
              )}
              {/* Sección de géneros */}
              <li className="mt-4 mb-6">
                <h3 className="text-base font-medium text-purple-400 mb-2">Géneros</h3>
                <div
                  className="2xl:max-h-500 max-h-96 overflow-y-auto border border-purple-400/20 rounded-lg p-2 scrollbar-transparent"
                >
                  <ul className="space-y-2">
                    {generos.map((genre) => (
                      <li
                        key={genre}
                        onClick={() => goToGenreBooks(genre)}
                        className="text-left text-sm text-white cursor-pointer hover:text-purple-400"
                        role="button"
                        tabIndex="0"
                        aria-label={`Ver libros del género ${genre}`}
                        onKeyDown={(e) => handleKeyDown(genre, e)}
                      >
                        {genre}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>
          </div>
          {/* Botón para cerrar el sidebar en dispositivos móviles */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden text-white hover:text-purple-400 self-end"
            aria-label="Cerrar menú"
            tabIndex="0"
            onKeyDown={(e) => e.key === 'Enter' || e.key === ' ' ? setIsSidebarOpen(false) : null}
          >
            Cerrar
          </button>
        </div>
      </nav>

      {/* Contenido principal */}
      <div
        className="flex flex-col items-center justify-center w-full ml-auto mr-auto md:pl-64 relative"
        role="region"
        aria-label="Contenido principal"
      >
        {/* Botón de cerrar sesión */}
        <button
          className="absolute top-6 right-6 flex items-center gap-2 text-red-400 hover:text-red-600 transition duration-200"
          onClick={() => {
            localStorage.removeItem("token");
            window.location.reload();
          }}
          aria-label="Cerrar sesión"
        >
          <FaSignOutAlt className="text-xl" />
          <span className="font-medium">Salir</span>
        </button>

        {/* Botón para abrir el sidebar en dispositivos móviles */}
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="absolute top-6 left-6 md:hidden z-10 bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
        </button>

        <span className="sr-only">Invision</span>

        <h1 className="text-6xl font-medium mb-8" aria-hidden="true">
          <span className="text-purple-400">I</span>
          <span className="text-purple-300">n</span>
          <span className="text-purple-200">v</span>
          <span className="text-purple-300">i</span>
          <span className="text-purple-400">s</span>
          <span className="text-purple-300">i</span>
          <span className="text-purple-200">o</span>
          <span className="text-purple-300">n</span>
        </h1>

        <p className="text-purple-200 mb-8 text-lg">Biblioteca de audiolibros</p>

        {/* Barra de búsqueda */}
        <div className="w-full max-w-xl">
          <div className="relative">
            <div className="flex items-center border border-purple-400 rounded-full px-6 py-3 hover:shadow-lg hover:shadow-purple-500/20 focus-within:shadow-lg focus-within:shadow-purple-500/20 bg-slate-800">
              <Search className="w-5 h-5 text-purple-400 mr-3" aria-hidden="true" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full outline-none bg-transparent text-white placeholder-purple-300"
                placeholder="Busca tu próximo audiolibro..."
                aria-label="Buscar audiolibros"
              />
              <Headphones
                className="w-5 h-5 text-purple-400 ml-3 cursor-pointer"
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Botón de búsqueda */}
          <div className="flex justify-center mt-8 space-x-4">
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 hover:shadow-lg hover:shadow-purple-500/20"
              aria-label="Realizar búsqueda"
            >
              Buscar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Searcher;